import apiClient from './apiClient';

// Helper function to get the base URL for file uploads
const getBaseUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
  // Remove /api/v1 suffix to get the base URL
  return apiUrl.replace('/api/v1', '');
};

// Helper function to convert relative photo URLs to full URLs
const getFullPhotoUrl = (photoUrl: string | null | undefined): string | undefined => {
  if (!photoUrl) return undefined;
  if (photoUrl.startsWith('http')) return photoUrl;
  return `${getBaseUrl()}${photoUrl}`;
};

export const userService = {
  // Get all trainers
  getAllTrainers: async () => {
    console.log('🔍 [userService] Calling /users/trainers endpoint...');
    const response = await apiClient.get('/users/trainers');
    console.log('🔍 [userService] Raw response from /users/trainers:', response.data);
    
    // Handle both wrapped and unwrapped responses
    if (Array.isArray(response.data)) {
      console.log('🔍 [userService] Response is array, returning directly');
      return response.data;
    }
    const result = response.data?.data || response.data || [];
    console.log('🔍 [userService] Processed result:', result);
    return result;
  },

  // Get all members
  getAllMembers: async () => {
    const response = await apiClient.get('/users');
    // Backend already returns only members with expiration checking
    const data = Array.isArray(response.data) 
      ? response.data 
      : response.data?.data || [];
    
    // Convert relative photo URLs to full URLs
    return data.map((member: any) => ({
      ...member,
      profilePhotoUrl: getFullPhotoUrl(member.profilePhotoUrl)
    }));
  },

  // Get trainer details
  getTrainerDetails: async (trainerId: string) => {
    const response = await apiClient.get(`/users/${trainerId}`);
    return response.data?.data || response.data;
  },

  // Get member details
  getMemberDetails: async (memberId: string) => {
    const response = await apiClient.get(`/users/${memberId}`);
    const data = response.data?.data || response.data;
    
    // Photo URL can be in either profilePhotoUrl or profile.photo
    const photoUrl = data?.profilePhotoUrl || data?.profile?.photo;
    
    // Convert relative photo URLs to full URLs
    const profilePhotoUrl = getFullPhotoUrl(photoUrl);

    return {
      ...data,
      profilePhotoUrl,
    };
  },

  // Get member's active programs
  getMemberActivePrograms: async (memberId: string) => {
    try {
      const response = await apiClient.get(`/programs/member/${memberId}/active`);
      const programs = Array.isArray(response.data) ? response.data : response.data?.data || [];
      
      // Convert cover photo URLs to full URLs if needed
      const mapped = programs.map((program: any) => ({
        ...program,
        program: {
          ...program.program,
          imageUrl: getFullPhotoUrl(program.program?.imageUrl)
        }
      }));
      
      return mapped;
    } catch (error) {
      console.error('Error fetching member active programs:', error);
      return [];
    }
  },

  // Get trainer stats
  getTrainerStats: async () => {
    try {
      const trainers = await userService.getAllTrainers();
      const totalTrainers = trainers.length;
      const activeTrainers = trainers.filter((t: any) => t.accountStatus === 'Active').length;
      const pendingTrainers = trainers.filter((t: any) => t.accountStatus === 'Pending').length;
      const avgRating = trainers.length > 0 
        ? (trainers.reduce((sum: number, t: any) => sum + (t.rating || 0), 0) / trainers.length).toFixed(1)
        : 0;

      return {
        totalTrainers,
        activeTrainers,
        pendingTrainers,
        avgRating,
      };
    } catch (error) {
      console.error('Error fetching trainer stats:', error);
      return {
        totalTrainers: 0,
        activeTrainers: 0,
        pendingTrainers: 0,
        avgRating: 0,
      };
    }
  },

  // Get member stats
  getMemberStats: async () => {
    try {
      const members = await userService.getAllMembers();
      const totalMembers = members.length;
      const activeMembers = members.filter((m: any) => m.membershipStatus === 'Active').length;
      const newThisMonth = members.filter((m: any) => {
        const createdAt = new Date(m.createdAt);
        const now = new Date();
        return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
      }).length;

      return {
        totalMembers,
        activeMembers,
        newThisMonth,
      };
    } catch (error) {
      console.error('Error fetching member stats:', error);
      return {
        totalMembers: 0,
        activeMembers: 0,
        newThisMonth: 0,
      };
    }
  },

  // Create a new member
  createMember: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/users/register', formData);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  // Update member profile
  updateMember: async (memberId: string, formData: FormData) => {
    try {
      const response = await apiClient.patch(`/users/${memberId}`, formData);
      const data = response.data?.data || response.data;
      
      // Convert relative photo URLs to full URLs
      const profilePhotoUrl = getFullPhotoUrl(data.profilePhotoUrl);

      return {
        ...data,
        profilePhotoUrl,
      };
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  },

  // Reset member password
  resetMemberPassword: async (memberId: string, newPassword: string) => {
    try {
      const response = await apiClient.patch(`/users/${memberId}/reset-password`, {
        newPassword
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error resetting member password:', error);
      throw error;
    }
  },

  // Get gym-specific members
  getGymMembers: async (gymId: string) => {
    try {
      const response = await apiClient.get(`/gyms/${gymId}/members`);
      
      // Handle the response structure: {success: true, message: 'Success', data: {count: X, members: [...]}}
      const apiData = response.data?.data || response.data; // Handle wrapped response
      const data = apiData?.members || apiData || []; // Handle both wrapped and direct array responses
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.warn('Expected members array but received:', typeof data, data);
        return [];
      }
      
      // Convert relative photo URLs to full URLs and format data
      return data.map((member: any) => ({
        _id: member._id,
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        phone: member.phone || '',
        membershipStatus: 'Active', // Default for gym members
        joinedDate: member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
        profilePhotoUrl: getFullPhotoUrl(member.profilePhotoUrl),
        createdAt: member.createdAt,
      }));
    } catch (error) {
      console.error('Error fetching gym members:', error);
      return [];
    }
  },

  // Get gym-specific trainers
  getGymTrainers: async (gymId: string) => {
    try {
      const response = await apiClient.get(`/gyms/${gymId}/trainers`);
      
      // Handle the response structure: {success: true, message: 'Success', data: {count: X, trainers: [...]}}
      const apiData = response.data?.data || response.data; // Handle wrapped response
      const data = apiData?.trainers || apiData || []; // Handle both wrapped and direct array responses
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.warn('Expected trainers array but received:', typeof data, data);
        return [];
      }
      
      // Convert relative photo URLs to full URLs and format data
      return data.map((trainer: any) => ({
        _id: trainer._id,
        firstName: trainer.firstName || '',
        lastName: trainer.lastName || '',
        email: trainer.email || '',
        phone: trainer.phone || '',
        accountStatus: 'Active', // Default for gym trainers
        joinedDate: trainer.createdAt ? new Date(trainer.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
        profilePhotoUrl: getFullPhotoUrl(trainer.profilePhotoUrl),
        createdAt: trainer.createdAt,
      }));
    } catch (error) {
      console.error('Error fetching gym trainers:', error);
      return [];
    }
  },

  // Update trainer status (approve/reject)
  updateTrainerStatus: async (trainerId: string, status: 'Active' | 'Pending' | 'Banned') => {
    try {
      const response = await apiClient.patch(`/users/${trainerId}/status`, {
        status
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating trainer status:', error);
      throw error;
    }
  },
};
